import type { TemplateResult } from 'lit'
import type { ResultQuestion, SurveyResult } from '../contract'
import { css, html, nothing } from 'lit'
import { descText, fmtDate } from './frame'
import { iconSelected, iconThankYouStamp } from './icons'

interface ResultBar { label: string, percent: number, isSelected: boolean }
export interface ResultBlock { seq: number, text: string, bars: ResultBar[] }

// 只有帶比例的選擇題會走到這裡，clamp 到 0–100（缺值/NaN/Infinity → 0）：percent 的範圍未定，
// 負值/極大值會撐爆進度條版面、誤導讀者對投票結果的認知。
function clampPercent(n: number | undefined): number {
  const v = n ?? 0
  return Number.isFinite(v) ? Math.min(100, Math.max(0, Math.round(v))) : 0
}

// 結果頁只收到帶比例的選擇題：沒有比例的題目（個資題）與沒有 options 的題目（文字題、日期題）
// 在 api.ts 的 parseResult 就已略過，所以這裡不做題目層的特化（question-precision.ts 那種），
// 也不處理 answer / date。ResultQuestion 沒有 question id（見 contract.ts），認不出是哪一題。
export function resultBlocks(result: SurveyResult): ResultBlock[] {
  return result.questions.map((q: ResultQuestion) => ({
    seq: q.seq,
    text: q.text,
    bars: (q.options ?? []).map(o => ({ label: o.content, percent: clampPercent(o.percent), isSelected: o.is_selected })),
  }))
}

// 結果頁（Figma 303:2966 / 303:3155 / 315:5230）：統計時間 meta → 各題（Q-num + 題目 +
// Answer Result 列），題與題之間分隔線（內容上下各 20px）。Q-num / 題目沿用 questionStyles
// 的 .q-row/.q-num/.q-title（同一 shadow root）。
export const resultsStyles = css`
  .results-meta {
    margin: 0 0 8px;
    font-size: 14px;
    letter-spacing: 0.5px;
    color: var(--mp-text-meta);
  }
  .result-block { padding: 20px 0; }
  .result-block:first-of-type { padding-top: 12px; }
  .result-block + .result-block { border-top: 1px solid var(--mp-border-sub); }
  /* Answer Result（Figma 303:2010）：14px 標籤在上、下行 = 7px 軌道（track = bg/Soft）+
     右對齊 16px 粗體百分比；最高票 = brand 色；自己選的 → 百分比右側 16px 圓形勾勾。
     bar 與百分比 gap：mobile 12 / desktop 16（Figma 315:5230 / 303:2966）；列間 gap 8。 */
  .answer-result { margin-top: 8px; }
  .ar-label { font-size: 14px; color: var(--mp-text-secondary); letter-spacing: 0.5px; }
  .ar-row { display: flex; align-items: center; gap: 12px; margin-top: 4px; }
  .ar-track { flex: 1 1 auto; min-width: 0; max-width: 500px; height: 7px; overflow: hidden; background: var(--mp-track); }
  .ar-fill { height: 100%; background: var(--mp-scrim-soft); }
  /* 最高票 fill = Figma border/focus（IP weak 80%，各 IP node 322:7026/11812… 皆同），非實心 brand。 */
  .ar-fill.is-top { background: var(--mp-brand-weak); }
  .ar-meta { flex: none; display: flex; align-items: center; gap: 4px; }
  .ar-pct { min-width: 33px; text-align: right; font-size: 16px; font-weight: 700; color: var(--mp-text-meta); }
  .ar-pct.is-top { color: var(--mp-brand); }
  .ar-check { width: 16px; height: 16px; flex: none; color: var(--mp-brand); }
  @container frame (min-width: 640px) {
    .ar-row { gap: 16px; }
  }
  /* 致謝畫面在卡片剩餘高度內**垂直**置中（Figma 322:5330 系列：內容區中線對齊）。
     thank-you 的 scroll owner 是 frame 而非 body（geometry.ts 的 SCROLL_OWNER），那條路徑
     不給 .frame-body 任何 flex 宣告，於是致謝內容會貼在 body 頂、下方留一大塊空白（LAB-2034
     圖二）。只有固定面板高的 popup 版型有剩餘高度可置中；inline 致謝面板跟內容長（geometry.ts
     的 PANEL），沒有剩餘高度，這段宣告在那裡等於不作用。
     這裡補上結構宣告；inline 版型的 .frame-body 已是 flex column，只差對齊方式。
     置中用 .thankyou 的 auto margin 而不是容器的 justify-content: center —— 兩者在空間夠時
     等價，差別在空間不夠的時候：auto margin 解析成 0，內容照常從頂端排下去；justify-content
     的 center 會把溢出平均分到兩端，上緣那半捲不回來（矮 viewport 會踩到 —— 面板高吃 90dvh
     上限）。safe center 也能避開，但它要 Safari 16 才有，正好是本 repo baseline 的下緣，
     而 auto margin 是 flexbox 本來就有的行為，不必賭。 */
  .frame--thank-you {
    display: flex;
    flex-direction: column;
  }
  .frame--thank-you .frame-body {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    flex-direction: column;
  }
  .frame--thank-you .thankyou {
    margin-top: auto;
    margin-bottom: auto;
  }
  /* 不呈現結果 → 致謝畫面（Figma 322:5330/5365/5417/5631，四變體同一內容區）：
     置中直排 gap 12、66px 插章、20px SemiBold 致謝文案、描邊「結束」鈕。 */
  .thankyou {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 16px 0 16px;
    text-align: center;
  }
  .thankyou-stamp { width: 66px; height: 66px; }
  .thankyou-msg {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 30px;
    letter-spacing: 1.2px;
    color: var(--mp-text-secondary);
  }
  /* 「結束」與「看結果」都是 Size=L 的描邊鈕（controls.ts），這裡只補設計稿的寬度
     （Figma 322:5348：140）。窄容器不降級成 Size=M —— action bar 才降。 */
  .thankyou .btn-secondary {
    min-width: 140px;
  }
`

function topBarIndex(bars: ResultBar[]): number {
  let top = -1
  let max = 0
  bars.forEach((b, i) => {
    if (b.percent > max) {
      max = b.percent
      top = i
    }
  })
  return top
}

function answerResultRow(bar: ResultBar, isTop: boolean): TemplateResult {
  return html`
    <div class="answer-result">
      <span class="ar-label">${bar.label}</span>
      <div class="ar-row">
        <div class="ar-track"><div class="ar-fill ${isTop ? 'is-top' : ''}" style="width:${bar.percent}%"></div></div>
        <span class="ar-meta">
          <span class="ar-pct ${isTop ? 'is-top' : ''}">${bar.percent}%</span>
          ${bar.isSelected ? iconSelected() : nothing}
        </span>
      </div>
    </div>
  `
}

export interface ThankYouOpts {
  // popup 形式才傳：顯示描邊「結束」鈕（關 popup，或 sidebar 無結果時令 widget 消失）。
  onClose?: (() => void) | undefined
  // 頁面殘留/收合的結束畫面才傳：顯示描邊「看結果」鈕，點擊重開結果 popup。
  onViewResult?: (() => void) | undefined
}

// 送出後的致謝畫面（Figma 322:5330 系列）。依 opts 決定鈕：onViewResult→「看結果」、onClose→「結束」；
// 兩者皆無 → 純致謝無鈕（inline 無結果的 in-flow 結束畫面）。
export function thankYouView(opts: ThankYouOpts = {}): TemplateResult {
  return html`
    <div class="thankyou">
      ${iconThankYouStamp()}
      <p class="thankyou-msg">感謝您完成填寫</p>
      ${opts.onViewResult ? html`<button type="button" class="btn-secondary thankyou-view-result" @click=${opts.onViewResult}>看結果</button>` : nothing}
      ${opts.onClose ? html`<button type="button" class="btn-secondary thankyou-close" @click=${opts.onClose}>結束</button>` : nothing}
    </div>
  `
}

export function resultsView(
  result: SurveyResult | undefined,
  opts: { ended?: boolean, onClose?: () => void, onViewResult?: () => void, description?: string | null } = {},
): TemplateResult {
  if (!result) {
    // ended 只在這裡有意義：問卷已結束時說明沒有結果資料，作答成功但後端不回傳統計
    // （不呈現結果的問卷）則走致謝畫面。下面的統計區塊不必再分 ended —— 已結束的那幾格
    // popup 放的是致謝殼，呼叫端因此不會把 result 交過來（見 post-submit.ts 的 VIEWS 與
    // survey-element 的 resultsRender）。
    if (opts.ended)
      return html`<p class="muted">此問卷已結束,目前沒有結果資料。</p>`
    return thankYouView({ onClose: opts.onClose, onViewResult: opts.onViewResult })
  }
  const blocks = resultBlocks(result)
  const description = descText(opts.description)
  // A syntactically valid result may still contain no displayable questions. Treat it as the
  // thank-you phase before rendering stats/description so mobile thank-you matches Figma.
  if (blocks.length === 0)
    return thankYouView({ onClose: opts.onClose, onViewResult: opts.onViewResult })
  return html`
    <p class="results-meta">統計時間：${fmtDate(result.stats_from)}~${fmtDate(result.stats_to)}</p>
    ${description ? html`<p class="frame-desc frame-desc--body">${description}</p>` : nothing}
    ${blocks.map((b, i) => {
      const topIdx = topBarIndex(b.bars)
      return html`
        <div class="result-block">
          <div class="q-row">
            <span class="q-num">Q${i + 1}</span>
            <div class="q-body">
              <h2 class="q-title">${b.text}</h2>
              ${b.bars.map((bar, j) => answerResultRow(bar, j === topIdx))}
            </div>
          </div>
        </div>
      `
    })}
  `
}
